import React, { useEffect } from 'react'
import styled from 'styled-components'
import { Link, Image, RightCol, AppBar, Toolbar } from './MobileNavigation'
import logo from '../../../images/logo.svg'
import theme from '../../styling/theme'
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from 'body-scroll-lock'
import BurgerMenuLink from './BurgerMenuLink'

interface BurgerMenuProps {
  links: {
    title: string
    slug: string
  }[]
  closeMenu: any
  scrollValue: number
  menuIsOpen: boolean
  ipaLogo?: string
  ipaLogoUrl?: string
}

const BurgerMenu = ({
  links,
  closeMenu,
  scrollValue,
  menuIsOpen,
  ipaLogo,
  ipaLogoUrl = 'https://ip-a.co.uk/',
}: BurgerMenuProps) => {
  const basePath =
    process.env.BASE === '/' || !process.env.BASE ? '/' : process.env.BASE

  useEffect(() => {
    if (menuIsOpen) {
      const scrollable = document.getElementById('menu-mob-scrollable')
      const body = document.querySelector('body')

      if (scrollable) {
        disableBodyScroll(scrollable)
      }

      return () => {
        if (body) {
          enableBodyScroll(body)
        }

        clearAllBodyScrollLocks()
        document.documentElement.scrollTop = document.body.scrollTop =
          scrollValue
      }
    }
  }, [menuIsOpen, scrollValue])

  return (
    <>
      {menuIsOpen && (
        <Container>
          <AppBar>
            <Toolbar transparent>
              <Link href={basePath}>
                <Image
                  src={logo}
                  alt="RDA"
                  style={{ height: '40px', width: 'auto' }}
                />
              </Link>

              <RightCol>
                <Menu onClick={closeMenu} aria-label="Close menu">
                  <Line first />
                  <Line second />
                  <Line third />
                </Menu>
              </RightCol>
            </Toolbar>
          </AppBar>

          <LinksContainer id="menu-mob-scrollable">
            <LinkContainer>
              {links.map((edge, key) => (
                <BurgerMenuLink link={edge.slug} key={key} text={edge.title} />
              ))}

              {ipaLogo && (
                <IpaMobileNavItem
                  href={ipaLogoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Visit the IPA website"
                  onClick={closeMenu}
                >
                  <img src={ipaLogo} alt="IPA Logo" />
                </IpaMobileNavItem>
              )}
            </LinkContainer>
          </LinksContainer>
        </Container>
      )}
    </>
  )
}

export default BurgerMenu

const LinkContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 200vh;
  z-index: 999;
  background: ${theme.text.white};
`

const LinksContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  background: white;
  padding-bottom: 60px;
`

const IpaMobileNavItem = styled.a`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 22px 24px 18px;
  text-decoration: none !important;
  width: 100%;

  img {
    width: auto;
    max-width: 185px;
    height: auto;
    display: block;
  }

  &:hover,
  &:active,
  &:focus {
    text-decoration: none !important;
  }
`

interface ILine {
  first?: boolean
  second?: boolean
  third?: boolean
}

const Line = styled.div<ILine>`
  width: 27px;
  height: 2px;
  margin: ${props => props.second && '5px 0'};
  background-color: ${theme.brand.blue};
  transform-style: preserve-3d;
  transition: all 200ms ease 0s;
  will-change: transform;
  backface-visibility: hidden;

  ${props =>
    props.first &&
    `
      transform: translateY(7px) rotate(45deg);
    `}

  ${props =>
    props.second &&
    `
      opacity: 0;
    `}

  ${props =>
    props.third &&
    `
      transform: translateY(-7px) rotate(-45deg);
    `}
`

const Menu = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  cursor: pointer;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  z-index: 5;
`